import {
  IRequestedAmount,
  IRequestHistorialsService,
  ISolCustomerCommodityType,
  ISolCustomerCurrency,
  ISolCustomerDenominationQuantity,
  ISolCustomerPack,
  ISolCustomerPackQuantity,
  ISolCustomerSupply,
} from "@/entities";

interface INormalizeRequestedAmountsParams {
  packs?: ISolCustomerPack[];
  currencies?: ISolCustomerCurrency[];
}

const getNumberField = (source: Record<string, unknown>, key: string): number | undefined => {
  const value = source[key];
  return typeof value === "number" && !Number.isNaN(value) ? value : undefined;
};

export const getOrderDetailStringField = (source: unknown, key: string): string | undefined => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" ? value : undefined;
};

export const resolveOrderDetailSource = (
  parsed: unknown
): Partial<IRequestHistorialsService> | null => {
  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const source = parsed as Record<string, unknown>;
  const candidates = [
    source,
    source.service,
    source.order,
    source.payload,
    source.request,
    source.data,
  ];
  const expectedKeys = [
    "serviceType",
    "productCode",
    "requestedAmounts",
    "requestInitialTime",
    "entityDescription",
    "servicePointDescription",
    "address",
    "applicant",
    "faultDescription",
    "assistanceObservation",
    "supplies",
  ];

  const validCandidate = candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      expectedKeys.some((key) => Object.prototype.hasOwnProperty.call(candidate, key))
  );

  return (validCandidate || source) as Partial<IRequestHistorialsService>;
};

const normalizeDenominationQuantities = (value: unknown): ISolCustomerDenominationQuantity[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized: ISolCustomerDenominationQuantity[] = [];
  value.forEach((item) => {
    const source = item as Record<string, unknown>;
    const denominationId =
      getOrderDetailStringField(source, "denominationId") ||
      getOrderDetailStringField(source, "id");
    if (!denominationId) {
      return;
    }

    const denominationQuantityFromField = getNumberField(source, "denominationQuantity");
    const denominationQuantity =
      denominationQuantityFromField ?? getNumberField(source, "quantity");

    normalized.push({
      denominationId,
      denominationQuantity,
    });
  });

  return normalized;
};

const normalizePackQuantities = (value: unknown): ISolCustomerPackQuantity[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized: ISolCustomerPackQuantity[] = [];
  value.forEach((item) => {
    const source = item as Record<string, unknown>;
    const packId =
      getOrderDetailStringField(source, "packId") || getOrderDetailStringField(source, "id");
    if (!packId) {
      return;
    }

    const packQuantityFromField = getNumberField(source, "packQuantity");
    const packQuantity = packQuantityFromField ?? getNumberField(source, "quantity");

    normalized.push({
      packId,
      packQuantity,
    });
  });

  return normalized;
};

const resolveCurrencyFromPacks = (
  packs: ISolCustomerPackQuantity[],
  definitions?: ISolCustomerPack[],
  currencies?: ISolCustomerCurrency[]
): { currencyId?: string; currencyIsoCode?: string; currencySymbol?: string } => {
  const firstPack = packs.find((pack) => !!pack.packId);
  if (!firstPack) {
    return {};
  }

  const packDefinition = definitions?.find((pack) => pack.id === firstPack.packId);
  const firstDenomination = packDefinition?.denominations?.[0];
  const currencyIsoCode = firstDenomination?.currencyIsoCode;
  const currencySymbol = firstDenomination?.currencySymbol;
  const currencyId = currencies?.find(
    (currency) => currency.isoCode === currencyIsoCode
  )?.currencyId;

  return {
    currencyId,
    currencyIsoCode,
    currencySymbol,
  };
};

const getTotalAmountFromPacks = (
  packs: ISolCustomerPackQuantity[],
  definitions?: ISolCustomerPack[]
): number | undefined => {
  if (!packs.length) {
    return undefined;
  }

  let totalAmount = 0;
  packs.forEach((packQty) => {
    const packDefinition = definitions?.find((pack) => pack.id === packQty.packId);
    if (!packDefinition) {
      return;
    }

    const packAmount = (packDefinition.denominations || []).reduce((acc, denomination) => {
      const denQty = Number(denomination.quantity || 0);
      const denValue = Number(denomination.value || 0);
      return acc + denQty * denValue;
    }, 0);

    totalAmount += packAmount * Number(packQty.packQuantity || 0);
  });

  return totalAmount || undefined;
};

export const normalizeOrderRequestedAmounts = (
  value: unknown,
  params: INormalizeRequestedAmountsParams = {}
): IRequestedAmount[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized: IRequestedAmount[] = [];
  value.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const source = item as Record<string, unknown>;
    const packs = normalizePackQuantities(source.packs);
    const packCurrency = resolveCurrencyFromPacks(packs, params.packs, params.currencies);
    const currencyId = getOrderDetailStringField(source, "currencyId") || packCurrency.currencyId;
    const currency = params.currencies?.find((c) => String(c.currencyId) === String(currencyId));
    const totalAmountFromPacks = getTotalAmountFromPacks(packs, params.packs);
    const totalAmount = getNumberField(source, "totalAmount");

    normalized.push({
      currencyId,
      currencyIsoCode:
        getOrderDetailStringField(source, "currencyIsoCode") ||
        packCurrency.currencyIsoCode ||
        currency?.isoCode,
      currencySymbol:
        getOrderDetailStringField(source, "currencySymbol") ||
        packCurrency.currencySymbol ||
        currency?.symbol,
      commodity: getOrderDetailStringField(source, "commodity"),
      commodityCode: getOrderDetailStringField(source, "commodityCode"),
      commodityDescription: getOrderDetailStringField(source, "commodityDescription"),
      totalAmount: totalAmount ?? totalAmountFromPacks,
      denominations: normalizeDenominationQuantities(source.denominations),
      packs,
    });
  });

  return normalized.length ? normalized : undefined;
};

export const normalizeOrderSupplies = (
  value: unknown,
  commodityTypes?: ISolCustomerCommodityType[]
): ISolCustomerSupply[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized: ISolCustomerSupply[] = [];
  value.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const source = item as Record<string, unknown>;
    const commodityTypeId = getOrderDetailStringField(source, "commodityTypeId");
    const commodityId = getOrderDetailStringField(source, "commodityId");
    if (!commodityTypeId || !commodityId) {
      return;
    }

    const commodityType = commodityTypes?.find((type) => type.id === commodityTypeId);
    const commodity = commodityType?.commodities?.find(
      (currentCommodity) => currentCommodity.id === commodityId
    );

    normalized.push({
      commodityTypeId,
      commodityTypeDescription:
        getOrderDetailStringField(source, "commodityTypeDescription") || commodityType?.description,
      commodityId,
      commodityDescription:
        getOrderDetailStringField(source, "commodityDescription") || commodity?.description,
      quantity: getNumberField(source, "quantity"),
    });
  });

  return normalized.length ? normalized : undefined;
};
